import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {colors} from 'shells/colors/colors';
import {NEW_EDITION_ID} from 'shells/edition-thumbnail-list/edition-thumbnail-list';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {LockRegularIcon} from 'shells/icon';
import {MailerRegularAiIcon} from 'shells/icon/svg/mailer-regular-ai-icon';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {MarketingOutreachSelectList} from 'shells/marketing-outreach-select-list/marketing-outreach-select-list';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {Body} from 'shells/typography/body';

import {AiFeatureType} from 'nutshell-graphql-types';
import type {
    GetRecentEditions as GetRecentEditionsQuery,
    GetRecentEditionsVariables as GetRecentEditionsQueryVariables,
    GetRecentEditions_recentEditions as RecentEditions,
    GetEditionStarters as GetEditionStartersQuery,
    GetEditionStarters_editionStarters as EditionStarters,
    EditionStubFragment as DripEdition,
    GetRecentUserTemplates as GetUserTemplatesQuery,
    GetRecentUserTemplatesVariables as GetUserTemplatesQueryVariables,
    GetRecentUserTemplates_recentUserTemplates as RecentUserTemplates,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useIsAiFeatureEnabled} from '../../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {useGenerateAiEmailFromTemplate} from '../ai-design/graphql/hooks/use-generate-ai-email-from-template';
import {NutshellAiEmptyState} from '../broadcasts/ai-design/nutshell-ai-empty-state';
import {DripSequenceEditionStubsQuery} from '../graphql-wrappers/drip-sequence-edition/drip-sequence-edition-stubs-query';
import {FAKE_TEMPLATE_ID} from '../templates/new-template-modal';

import {ComparisonFieldsSelectModal} from './a-b-testing/comparison-fields-select-modal';
import {EditionTypeSelection} from './edition-type-selection';
import GET_EDITION_STARTERS_QUERY from './graphql/queries/get-edition-starters.graphql';
import GET_RECENT_EDITIONS_QUERY from './graphql/queries/get-recent-editions.graphql';
import GET_RECENT_USER_TEMPLATES_QUERY from './graphql/queries/get-recent-user-templates.graphql';
import {useGenerateAiEmailTemplate} from './prepare-to-send-page/graphql/hooks/use-generate-ai-email-template';

export type CampaignModalType =
    | 'broadcasts'
    | 'newsletterEditions'
    | 'dripSequenceEditions'
    | 'campaignTemplates';

export const STANDARD_EDITION: EditionType = 'STANDARD';
export const A_B_TEST_EDITION: EditionType = 'A-B-TEST';

export type EditionType = 'STANDARD' | 'A-B-TEST';

type SelectedTab = 'recentMessages' | 'companyTemplates' | 'aiEmailTemplates';

type OwnProps = {
    isOpen: boolean;
    onClose: () => void;
    campaignId: string;
    type: CampaignModalType;
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onDuplicateEdition: (editionId: string, campaignId: string, name: string) => Promise<any>;
    toggleComparison?: (arg1: string[]) => void;
    dripSequenceId?: string;
    dripSequenceEditions?: DripEdition[];
    initialSelectedCategoryOrTemplateApiId?: string;
};

type Props = OwnProps & {
    dripSequenceEditions?: DripEdition[];
    isLoadingDripSequenceEditions: boolean;
    dripSequenceEditionsError: string | null | undefined;
    recentEditions: RecentEditions[];
    isLoadingRecentEditions: boolean;
    recentEditionsError: string | null | undefined;
    editionStarters: EditionStarters[]; // Nutshell Provided starter Templates,
    isLoadingEditionStarters: boolean;
    editionStartersError: string | null | undefined;
    recentUserTemplates: RecentUserTemplates[]; // User Created templates,
    isLoadingRecentUserTemplates: boolean;
    recentUserTemplatesError: string | null | undefined;
};

function NewEditionModalComponent(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [selectedEditionId, setSelectedEditionId] = React.useState<string>(NEW_EDITION_ID);
    const [selectedType, setSelectedType] = React.useState<EditionType>(STANDARD_EDITION);
    const [showTypeModal, setShowTypeModal] = React.useState<boolean>(false);
    const [showComparisonFieldsSelectModal, setShowComparisonFieldsSelectModal] =
        React.useState<boolean>(false);
    const [fieldsToCompare, setFieldsToCompare] = React.useState<string[]>([]);

    const [websiteUrl, setWebsiteUrl] = React.useState<string>('');
    const [websiteUrlError, setWebsiteUrlError] = React.useState<string | null | undefined>(null);
    const [campaignDescription, setCampaignDescription] = React.useState<string>('');

    const {generateAiEmailTemplate} = useGenerateAiEmailTemplate();
    const {generateAiEmailFromTemplate} = useGenerateAiEmailFromTemplate();
    const {user} = useGetSessionUser();
    const isFullMarketingUser = Boolean(
        user && user.permissions && user.permissions.canAccessFullCampaigns
    );

    const {isEnabled: isEmailCampaignBuilderEnabled, isLoading: isLoadingAiFeatureUsage} =
        useIsAiFeatureEnabled(AiFeatureType.EMAIL_CAMPAIGN_BUILDER);

    const [selectedTab, setSelectedTab] = React.useState<SelectedTab>('recentMessages');

    const recentEditions = props.recentEditions.map((edition) => {
        return {
            ...edition,
            thumbnailUrl:
                edition.emailTemplate && edition.emailTemplate.thumbnailUrl
                    ? edition.emailTemplate.thumbnailUrl
                    : undefined,
            campaignType: edition.campaign.type,
        };
    });

    const editionStarters = props.editionStarters.map((starter) => {
        return {
            id: starter.id,
            name: starter.name,
            thumbnailUrl: starter.thumbnailUrl || '',
            createdTime: starter.createdTime,
            sendTime: null,
            status: starter.status,
            campaignType: 'TEMPLATE',
        };
    });

    const recentUserTemplates = props.recentUserTemplates.map((edition) => {
        return {
            ...edition,
            thumbnailUrl:
                edition.emailTemplate && edition.emailTemplate.thumbnailUrl
                    ? edition.emailTemplate.thumbnailUrl
                    : undefined,
            campaignType: edition.campaign.type,
        };
    });

    const dripSequenceEditions = props.dripSequenceEditions
        ? props.dripSequenceEditions.map((edition) => {
              return {
                  ...edition,
                  id: edition.id,
                  name: edition.name,
                  thumbnailUrl:
                      edition.emailTemplate && edition.emailTemplate.thumbnailUrl
                          ? edition.emailTemplate.thumbnailUrl
                          : undefined,
                  createdTime: edition.createdTime,
                  sendTime: null,
                  status: edition.status,
                  campaignType: edition.campaign.type,
              };
          })
        : [];

    const selectableEditions = [
        ...recentEditions,
        ...editionStarters,
        ...dripSequenceEditions,
        ...recentUserTemplates,
    ];
    const selectedEdition = selectableEditions.find((edition) => edition.id === selectedEditionId);

    const userFacingNoun = getUserFacingNoun(props.type);

    const buttonText = !selectedEdition
        ? `Create new ${userFacingNoun}`
        : `Start from ${selectedEdition.name}`;
    const savingText = !selectedEdition
        ? `Creating new ${userFacingNoun}...`
        : `Starting from ${selectedEdition.name}...`;

    // If no edition(template, broadcast, etc) is selected, which only happens when on Your team's/Company templates tab and no templates exist,
    // we disable the button for creating a new edition. If any templates exist, the first in the list is auto-selected and the button is enabled.
    const isButtonDisabled =
        selectedTab === 'companyTemplates' && props.recentUserTemplates.length === 0;

    const recentEditionsList = [
        {
            id: 'recent-editions',
            title: 'Recent message',
            subtitle: 'Your recent outreach messages',
            collection: props.isLoadingRecentEditions
                ? undefined
                : [
                      {
                          id: NEW_EDITION_ID,
                          createNew: {
                              name: `Blank ${userFacingNoun}`,
                              height: recentEditions.length ? undefined : 300,
                          },
                      },
                      ...recentEditions.map((edition) => ({id: edition.id, edition})),
                  ],
            error: props.recentEditionsError,
            isLoading: props.isLoadingRecentEditions,
        },
    ];

    // Show AI option for broadcasts if base FF is enabled
    // Show AI option for all campaign types if V2 FF is enabled
    const showAiOption =
        NutClientConfig.hasAiEmailTemplateGenerationFF &&
        (props.type === 'broadcasts' || NutClientConfig.hasCampaignGeneratorV2FF);

    if (showAiOption) {
        recentEditionsList.push({
            id: 'ai-email-template',
            // @ts-expect-error
            title: isLoadingAiFeatureUsage ? (
                <LoadingText height={24} width={224} />
            ) : (
                <div className='flex align-center gap-8 color-ai-purple'>
                    <MailerRegularAiIcon size={24} />
                    <Body isBold={true} color='ai-purple'>
                        Create with Nutshell AI
                    </Body>
                    {!isEmailCampaignBuilderEnabled && (
                        <LockRegularIcon size={20} style={{color: colors.grey}} />
                    )}
                </div>
            ),
            isDisabled: !isEmailCampaignBuilderEnabled,
            collection: undefined,
            emptyStateComponent: (
                <NutshellAiEmptyState
                    websiteUrl={websiteUrl}
                    websiteUrlError={websiteUrlError}
                    campaignDescription={campaignDescription}
                    onWebsiteUrlChange={setWebsiteUrl}
                    onCampaignAboutChange={setCampaignDescription}
                    campaignModalType={props.type}
                />
            ),
        });
    }

    const recentUserTemplatesList = shouldShowTemplateList(
        props.type,
        isFullMarketingUser,
        recentUserTemplates.length
    )
        ? [
              {
                  id: 'templates',
                  title: 'Your team’s templates',
                  label: 'Company templates',
                  collection: props.isLoadingRecentUserTemplates
                      ? undefined
                      : recentUserTemplates.map((edition) => ({id: edition.id, edition})),
                  subtitle: `${recentUserTemplates.length} ${
                      recentUserTemplates.length === 1 ? 'template' : 'templates'
                  }`,
                  error: props.recentUserTemplatesError,
                  isLoading: props.isLoadingRecentUserTemplates,
                  emptyStateComponent: (
                      <EmptyStateSmall
                          title={
                              isFullMarketingUser
                                  ? 'Add marketing templates for your team'
                                  : 'Your team doesn’t have any templates'
                          }
                          subtitle={
                              isFullMarketingUser
                                  ? 'Add ready-to-go templates for your team to use in their outreach'
                                  : ''
                          }
                          cta={
                              // This button links to Templates page, so if we are already on the Templates page (campaignId is 'template') we don't want to show it
                              isFullMarketingUser && props.campaignId !== FAKE_TEMPLATE_ID ? (
                                  <Button
                                      variant='primary'
                                      size='big'
                                      onClick={() => appHistory.push('/email-marketing/templates')}
                                  >
                                      Learn more
                                  </Button>
                              ) : undefined
                          }
                          icon={
                              <img
                                  alt=''
                                  style={{height: 60, paddingRight: 0}}
                                  src='/include/images/empty-states/broadcast.svg'
                              />
                          }
                      />
                  ),
              },
          ]
        : [];

    if (dripSequenceEditions.length) {
        // @ts-expect-error
        recentEditionsList.unshift({
            id: 'drip-messages',
            title: 'Message in this drip',
            subtitle: 'Other messages in this drip sequence',
            collection: [
                {
                    id: NEW_EDITION_ID,
                    createNew: {
                        name: `Blank ${userFacingNoun}`,
                        height: dripSequenceEditions.length ? undefined : 300,
                    },
                },
                ...dripSequenceEditions.map((edition) => ({id: edition.id, edition})),
            ],
            error: props.dripSequenceEditionsError,
        });
    }

    const onCloseModal = () => {
        setIsSaving(false);
        props.onClose();
    };

    const handleEditionCreation = () => {
        // Default name for creating a new edition from scratch
        let initialEditionName = `Untitled ${userFacingNoun}`;
        if (selectedEdition && selectedEdition.campaignType !== 'TEMPLATE') {
            initialEditionName = `Copy of ${selectedEdition.name}`;
        }

        // Set that we are saving the new edition
        setIsSaving(true);
        if (selectedType === A_B_TEST_EDITION) {
            props.toggleComparison?.(fieldsToCompare);
        }

        if (!selectedEdition) {
            // If no selected edition we are creating a new one
            props
                .onCreateEdition(initialEditionName, props.campaignId)
                .then(() => {
                    setIsSaving(false);
                })
                .catch((err) => {
                    const error = safelyGetGraphQLErrorMessage(err);
                    setErrorMessage(error);
                    setIsSaving(false);
                });
        } else {
            // Duplicate the selected edition
            props.onDuplicateEdition(selectedEdition.id, props.campaignId, initialEditionName);
        }
    };

    const getButton = () => {
        if (selectedTab === 'aiEmailTemplates') {
            return (
                <SaveButton
                    // @ts-expect-error
                    type='button'
                    variant='ai-purple-dark'
                    size='big'
                    isSaving={isSaving}
                    disabled={!websiteUrl || !campaignDescription}
                    errorSaving={Boolean(websiteUrlError)}
                    buttonText='Preview and edit'
                    savingText='Generating preview...'
                    onClick={() => {
                        setIsSaving(true);
                        setWebsiteUrlError(null);

                        if (NutClientConfig.hasCampaignGeneratorV2FF) {
                            return generateAiEmailFromTemplate({
                                websiteUrl: websiteUrl,
                                userPrompt: campaignDescription,
                                originalCampaignId: isCampaignIdAnApiId(props.campaignId)
                                    ? props.campaignId
                                    : null,
                            })
                                .then(({data}) => {
                                    const aiCampaign =
                                        data.aiEmailTemplateGenerateFromTemplate.campaign;
                                    const firstEditionId = aiCampaign.editions.edges[0].node.id;

                                    appHistory.push(
                                        getAiCampaignRoute(
                                            props.campaignId,
                                            aiCampaign.id,
                                            firstEditionId,
                                            props.type
                                        )
                                    );
                                })
                                .catch((error) => {
                                    setIsSaving(false);
                                    setWebsiteUrlError(
                                        safelyGetGraphQLErrorMessage(error) ||
                                            'An unexpected error occurred. Please try again later.'
                                    );
                                });
                        }

                        return generateAiEmailTemplate({
                            websiteUrl: websiteUrl,
                            userPrompt: campaignDescription,
                        })
                            .then(({data}) => {
                                const aiCampaign = data.aiEmailTemplateGenerate.campaign;
                                const firstEditionId = aiCampaign.edition.id;

                                appHistory.push(
                                    getAiCampaignRoute(
                                        props.campaignId,
                                        aiCampaign.id,
                                        firstEditionId,
                                        props.type
                                    )
                                );
                            })
                            .catch((error) => {
                                setIsSaving(false);
                                setWebsiteUrlError(
                                    safelyGetGraphQLErrorMessage(error) ||
                                        'An unexpected error occurred. Please try again later.'
                                );
                            });
                    }}
                />
            );
        }

        if (props.type === 'campaignTemplates' || props.type === 'dripSequenceEditions') {
            return (
                <SaveButton
                    size='big'
                    isSaving={isSaving}
                    // @ts-expect-error
                    onClick={handleEditionCreation}
                    errorSaving={Boolean(errorMessage)}
                    buttonText={buttonText}
                    savingText={savingText}
                    disabled={isButtonDisabled}
                />
            );
        }

        return (
            <Button
                type='button'
                variant='save'
                size='big'
                onClick={() => setShowTypeModal(true)}
                disabled={isButtonDisabled}
            >
                {buttonText}
            </Button>
        );
    };

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                headerText={
                    props.dripSequenceEditions && props.dripSequenceEditions.length
                        ? 'Add another drip message'
                        : `Create a new ${userFacingNoun}`
                }
                onClose={onCloseModal}
                isWide={true}
                maxHeight={700}
            >
                <div
                    style={{
                        // Height offset with footer bar height
                        height: 'calc(100% - 99px)',
                    }}
                    className='mb-16'
                >
                    <MarketingOutreachSelectList
                        categorizedLists={
                            props.isLoadingEditionStarters ||
                            props.isLoadingDripSequenceEditions ||
                            props.isLoadingRecentEditions ||
                            props.isLoadingRecentUserTemplates
                                ? undefined
                                : [
                                      ...recentEditionsList,
                                      ...recentUserTemplatesList,
                                      ...bucketEditionStartersOnCampaignId(
                                          props.editionStarters,
                                          props.editionStartersError
                                      ),
                                  ]
                        }
                        initialSelectedCategoryId={props.initialSelectedCategoryOrTemplateApiId}
                        isLoading={
                            props.isLoadingEditionStarters ||
                            props.isLoadingDripSequenceEditions ||
                            props.isLoadingRecentEditions
                        }
                        selectedItemId={selectedEditionId}
                        setSelectedItemId={(newId?: string | null) =>
                            // should not happen, but use new edition id as a fallback
                            setSelectedEditionId(newId || NEW_EDITION_ID)
                        }
                        showEditionTypeIcon={true}
                        setSelectedTab={setSelectedTab}
                    />
                </div>
                <FooterBar
                    hasBorder={true}
                    left={
                        <Button variant='text' size='big' onClick={onCloseModal}>
                            Cancel
                        </Button>
                    }
                    right={getButton()}
                />
            </Modal>
            {showTypeModal && (
                <EditionTypeSelection
                    errorMessage={errorMessage}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                    setShowTypeModal={(shouldShow) => {
                        setErrorMessage(null);
                        setShowTypeModal(shouldShow);
                    }}
                    setShowComparisonFieldsSelectModal={setShowComparisonFieldsSelectModal}
                    type={props.type}
                    userFacingNoun={userFacingNoun}
                    handleEditionCreation={handleEditionCreation}
                    isCreating={isSaving}
                />
            )}
            {showComparisonFieldsSelectModal && (
                <ComparisonFieldsSelectModal
                    setShowComparisonFieldsSelectModal={setShowComparisonFieldsSelectModal}
                    fieldsToCompare={fieldsToCompare}
                    setFieldsToCompare={setFieldsToCompare}
                    userFacingNoun={userFacingNoun}
                    handleEditionCreation={handleEditionCreation}
                    isCreating={isSaving}
                />
            )}
        </>
    );
}

export function NewEditionModal(props: OwnProps) {
    const {
        data,
        loading: recentEditionsLoading,
        error: recentEditionsError,
    } = useQuery<GetRecentEditionsQuery, GetRecentEditionsQueryVariables>(
        GET_RECENT_EDITIONS_QUERY,
        {
            notifyOnNetworkStatusChange: true,
            variables: {
                limit: 100,
                dripSequenceId: props.dripSequenceId,
            },
        }
    );
    const recentEditions = data ? data.recentEditions : null;

    const {
        data: recentUserTemplatesData,
        loading: recentUserTemplatesLoading,
        error: recentUserTemplatesError,
    } = useQuery<GetUserTemplatesQuery, GetUserTemplatesQueryVariables>(
        GET_RECENT_USER_TEMPLATES_QUERY,
        {
            notifyOnNetworkStatusChange: true,
            variables: {
                limit: 100,
                status: 14, // 14 is edition status for published
            },
        }
    );
    const recentUserTemplates = recentUserTemplatesData
        ? recentUserTemplatesData.recentUserTemplates
        : null;

    const {
        data: editionStartersData,
        error: editionStartersError,
        loading: editionStartersLoading,
    } = useQuery<GetEditionStartersQuery, Record<any, any>>(GET_EDITION_STARTERS_QUERY, {
        notifyOnNetworkStatusChange: true,
    });
    const editionStarters = editionStartersData ? editionStartersData.editionStarters : null;

    return (
        <DripSequenceEditionStubsQuery
            skip={!props.dripSequenceId}
            // @ts-expect-error
            dripSequenceId={props.dripSequenceId ? props.dripSequenceId : undefined}
            isArchived={false}
        >
            {({
                editions,
                isLoading: dripSequenceEditionsLoading,
                error: dripSequenceEditionsError,
            }) => (
                <NewEditionModalComponent
                    {...props}
                    dripSequenceEditions={editions}
                    isLoadingDripSequenceEditions={dripSequenceEditionsLoading}
                    dripSequenceEditionsError={dripSequenceEditionsError}
                    recentEditions={recentEditions || []}
                    recentUserTemplates={recentUserTemplates || []}
                    isLoadingRecentUserTemplates={recentUserTemplatesLoading}
                    recentUserTemplatesError={safelyGetGraphQLErrorMessage(
                        // @ts-expect-error
                        recentUserTemplatesError
                    )}
                    isLoadingRecentEditions={recentEditionsLoading}
                    // @ts-expect-error
                    recentEditionsError={safelyGetGraphQLErrorMessage(recentEditionsError)}
                    editionStarters={editionStarters || []}
                    isLoadingEditionStarters={editionStartersLoading}
                    // @ts-expect-error
                    editionStartersError={safelyGetGraphQLErrorMessage(editionStartersError)}
                />
            )}
        </DripSequenceEditionStubsQuery>
    );
}

// @ts-expect-error
const getUserFacingNoun = (type) => {
    if (type === 'broadcasts') {
        return 'broadcast';
    } else if (type === 'dripSequenceEditions') {
        return 'drip message';
    } else if (type === 'campaignTemplates') {
        return 'template';
    } else {
        return 'edition';
    }
};

function bucketEditionStartersOnCampaignId(
    editionStarters: EditionStarters[],
    editionStartersError?: string | null
) {
    // @ts-expect-error
    const bucketedLists = [];

    editionStarters.forEach((edition) => {
        // If the campaign has not been bucketed yet, we will create a bucket
        // @ts-expect-error
        if (!bucketedLists.map((list) => list.id).includes(edition.campaignId)) {
            // Filter through all editions for campaign and map to correct type
            const campaignEditions = editionStarters
                .filter((starter) => starter.campaignId === edition.campaignId)
                .map((starter) => ({
                    id: starter.id,
                    edition: {
                        id: starter.id,
                        name: starter.name,
                        thumbnailUrl: starter.thumbnailUrl || '',
                        createdTime: starter.createdTime,
                        sendTime: null,
                        status: starter.status,
                        campaignType: 'TEMPLATE',
                        campaignName: starter.campaignName,
                        backgroundColor: starter.backgroundColor,
                    },
                }));

            // Create bucket for campaign with editions
            bucketedLists.push({
                id: edition.campaignId,
                // Add 'Templates' label above first category
                ...(bucketedLists.length === 0 ? {label: 'Templates'} : {}),
                title: edition.campaignName,
                subtitle: `${campaignEditions.length} templates`,
                collection: campaignEditions,
                error: editionStartersError,
            });
        }
    });

    // @ts-expect-error
    return bucketedLists;
}

function shouldShowTemplateList(
    messageType: CampaignModalType,
    isFullMarketingUser: boolean,
    numTemplates: number
) {
    // Don't show an empty state to users who can't create templates
    if (!isFullMarketingUser && numTemplates === 0) return false;

    return true;
}

function getCampaignTypeForRoute(type: CampaignModalType): string {
    switch (type) {
        case 'broadcasts':
            return 'broadcasts';
        case 'newsletterEditions':
            return 'newsletters';
        case 'dripSequenceEditions':
            return 'drip-sequences';
        case 'campaignTemplates':
            return 'templates';
    }
}

export function getAiCampaignRoute(
    originalCampaignId: string,
    aiCampaignId: string,
    firstEditionId: string,
    type: CampaignModalType
): string {
    const campaignTypeForRoute = getCampaignTypeForRoute(type);

    // For broadcasts and templates, use the AI campaign ID
    // For newsletters and drips, use the target campaign ID (props.campaignId)
    const campaignIdForRoute =
        type === 'broadcasts' || type === 'campaignTemplates' ? aiCampaignId : originalCampaignId;

    return `/marketing/email/${campaignTypeForRoute}/${campaignIdForRoute}/design/${firstEditionId}`;
}

function isCampaignIdAnApiId(campaignId: string): boolean {
    const idAndType = campaignId.split('-');

    if (idAndType.length !== 2) return false;

    const id = idAndType[0];
    if (id === undefined) return false;

    return !isNaN(parseInt(id, 10));
}
